import { useState } from 'react'
import { ChevronDown, ChevronRight, Info } from 'lucide-react'
import clsx from 'clsx'
import type { UnderwritingModel, UnderwritingSection, UnderwritingField } from '@/types/underwritingModels'

interface Props {
  model: UnderwritingModel
  dealId?: string
  values: Record<string, number | string>
  onFieldChange: (fieldId: string, value: number | string) => void
  readOnly?: boolean
}

function formatCalculatedValue(value: number | string | undefined, field: UnderwritingField): string {
  if (value === undefined || value === '') return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '—'
  if (field.suffix === '%') return `${num.toFixed(2)}%`
  if (field.prefix === '$') {
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`
    if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`
    return `$${num.toFixed(2)}`
  }
  return num.toLocaleString()
}

function FieldInput({ field, value, onChange, readOnly }: {
  field: UnderwritingField
  value: number | string | undefined
  onChange: (val: number | string) => void
  readOnly?: boolean
}) {
  if (field.type === 'calculated') {
    return (
      <div className="rounded border border-stone-700/40 bg-stone-900/50 px-2.5 py-1.5 text-[12px] text-amber-300 font-mono">
        {formatCalculatedValue(value, field)}
      </div>
    )
  }

  if (field.type === 'select') {
    return (
      <select
        value={value ?? field.defaultValue ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        className="w-full rounded border border-stone-700/50 bg-stone-900/60 px-2.5 py-1.5 text-[12px] text-stone-200 focus:border-amber-400/30 focus:outline-none disabled:opacity-50"
      >
        <option value="">Select...</option>
        {field.selectOptions?.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    )
  }

  if (field.type === 'text') {
    return (
      <input
        type="text"
        value={value ?? field.defaultValue ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        className="w-full rounded border border-stone-700/50 bg-stone-900/60 px-2.5 py-1.5 text-[12px] text-stone-200 focus:border-amber-400/30 focus:outline-none disabled:opacity-50"
        placeholder={field.tooltip ?? field.label}
      />
    )
  }

  // currency, percentage, number, date
  return (
    <div className="relative">
      {field.prefix && (
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-stone-500">{field.prefix}</span>
      )}
      <input
        type="number"
        value={value ?? field.defaultValue ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        disabled={readOnly}
        className={clsx(
          'w-full rounded border border-stone-700/50 bg-stone-900/60 py-1.5 text-[12px] text-stone-200 focus:border-amber-400/30 focus:outline-none disabled:opacity-50',
          field.prefix ? 'pl-5 pr-2.5' : 'px-2.5',
          field.suffix ? 'pr-6' : ''
        )}
        placeholder="0"
      />
      {field.suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-stone-500">{field.suffix}</span>
      )}
    </div>
  )
}

function SectionPanel({ section, values, onFieldChange, readOnly }: {
  section: UnderwritingSection
  values: Record<string, number | string>
  onFieldChange: (fieldId: string, value: number | string) => void
  readOnly?: boolean
}) {
  const [expanded, setExpanded] = useState(true)

  return (
    <div className="rounded-lg border border-amber-400/10 bg-black/20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.02] transition-colors"
      >
        {expanded ? <ChevronDown size={12} className="text-stone-500" /> : <ChevronRight size={12} className="text-stone-500" />}
        <span className="text-[12px] font-semibold text-stone-200">{section.label}</span>
        <span className="text-[9px] font-mono text-stone-600 uppercase">{section.fields.length} fields</span>
      </button>
      {expanded && (
        <div className="border-t border-amber-400/5 px-3 py-2.5">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {section.fields.map((field) => (
              <div key={field.id} className={field.type === 'calculated' ? '' : ''}>
                <div className="mb-0.5 flex items-center gap-1">
                  <label className="text-[10px] font-medium text-stone-400">
                    {field.label}
                    {field.required && <span className="text-amber-400 ml-0.5">*</span>}
                  </label>
                  {field.tooltip && (
                    <span title={field.tooltip} className="cursor-help">
                      <Info size={9} className="text-stone-600" />
                    </span>
                  )}
                  {field.assetClassSpecific && (
                    <span className="rounded bg-amber-400/10 px-1 py-px text-[7px] font-mono text-amber-400/60 uppercase">specific</span>
                  )}
                </div>
                <FieldInput
                  field={field}
                  value={values[field.id]}
                  onChange={(val) => onFieldChange(field.id, val)}
                  readOnly={readOnly}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function UnderwritingModelRenderer({ model, values, onFieldChange, readOnly }: Props) {
  const sortedSections = [...model.sections].sort((a, b) => a.order - b.order)

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-stone-600">
          {model.label}
        </span>
        <span className="text-[9px] text-stone-700">v{model.version}</span>
      </div>
      {sortedSections.map((section) => (
        <SectionPanel
          key={section.id}
          section={section}
          values={values}
          onFieldChange={onFieldChange}
          readOnly={readOnly}
        />
      ))}
    </div>
  )
}
